import { noop } from 'rxjs';
import clamp from 'lodash/clamp';

import { Component, forwardRef, Input, Output, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import 'style-loader!./steps.component.deep.scss';

export enum STEP_STATUS {
  ERROR = 'error',
  WAIT = 'wait',
  FINISH = 'finish',
  PROCESS = 'process',
}

export interface IStepOption {
  label: string;
  value?: string;
  status?: STEP_STATUS; // if status is not passed its set to a default value of 'wait'
  isDisabled?: boolean;
}

@Component({
  selector: 'steps',
  templateUrl: './steps.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StepsComponent),
      multi: true,
    },
  ],
})
export class StepsComponent {
  @Input() public readonly stepOptions: IStepOption[];
  @Input() public readonly shouldShowTitle: boolean = true;

  @Output() stepClicked: EventEmitter<string> = new EventEmitter<string>();

  public selectedStepValue: string;
  public currentIndex: number = 0;
  public progressInPercentage: number = 4; // starting with this to show some progress on the bar
  public readonly stepStatus: typeof STEP_STATUS = STEP_STATUS;

  private onChange: (_: string) => void;
  private onTouched: (_: string) => void;

  public onIndexChange(index: number): void {
    // Antd steps has an index binding so when index changes from within the component
    // we get corresponding step value and write it
    this.currentIndex = index;

    const step: IStepOption = this.stepOptions[index];
    const stepValue: string = step ? step.value : null;
    this.stepClicked.emit(stepValue);
    this.writeValue(stepValue);
  }

  public writeValue(selectedStepValue: string): void {
    if (selectedStepValue === undefined || !this.onChange) return;

    this.selectedStepValue = selectedStepValue;
    // When step value from outside the component changes so we update the currentIndex inside the component
    this.currentIndex = this.stepOptions.findIndex((step: IStepOption): boolean => step.value === selectedStepValue);
    this.progressInPercentage = this.getPercentage();
    this.onChange(selectedStepValue);
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = noop;
  }

  private getPercentage(): number {
    const percent: number = Math.round(((this.currentIndex + 1) / this.stepOptions?.length) * 100);
    return clamp(percent, 4, 100); // making sure the value is between 4-100 in all cases
  }
}
